<template>
  <md-autocomplete
    v-model="searchTerm"
    :md-options="optionLabels"
    :md-input-id="inputId"
    :md-input-name="name"
    :md-input-placeholder="placeholder"
    :md-fuzzy-search="false"
    @md-changed="onSearchChanged"
    @md-selected="onSelected">
    <label v-if="label" :for="inputId">{{ label }}</label>
    <template slot="md-autocomplete-empty" slot-scope="{ term }">
      {{ emptyMessage(term) }}
    </template>
  </md-autocomplete>
</template>

<script>
export default {
  name: 'SearchableSelect',
  props: {
    value: {
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Type to search'
    },
    noResultsText: {
      type: String,
      default: 'No matching options'
    }
  },
  data: () => ({
    searchTerm: '',
    suppressNextClear: false
  }),
  computed: {
    inputId() {
      return this.name || `searchable-select-${this._uid}`;
    },
    normalizedOptions() {
      const options = this.options.map((option) => {
        if (option !== null && typeof option === 'object') {
          return {
            value: option.value,
            label: String(option.label)
          };
        }

        return { value: option, label: String(option) };
      });

      const labelCounts = options.reduce((counts, option) => {
        counts[option.label] = (counts[option.label] || 0) + 1;
        return counts;
      }, {});

      return options.map((option) => ({
        ...option,
        displayLabel: labelCounts[option.label] > 1
          ? `${option.label} (${option.value})`
          : option.label
      }));
    },
    optionLabels() {
      return this.normalizedOptions.map((option) => option.displayLabel);
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (this.suppressNextClear && (value === null || value === undefined || value === '')) return;

        const option = this.normalizedOptions.find((item) => item.value === value);
        this.searchTerm = option ? option.displayLabel : '';
      }
    },
    options() {
      const option = this.normalizedOptions.find((item) => item.value === this.value);
      if (option) this.searchTerm = option.displayLabel;
    }
  },
  methods: {
    onSearchChanged(term) {
      const selected = this.normalizedOptions.find((option) => option.value === this.value);
      if (selected && selected.displayLabel === term) return;

      this.$emit('search', term);

      this.suppressNextClear = true;
      this.$emit('input', null);
      this.$nextTick(() => {
        this.suppressNextClear = false;
      });
    },
    onSelected(label) {
      const option = this.normalizedOptions.find((item) => item.displayLabel === label);
      if (option) this.$emit('input', option.value);
    },
    emptyMessage(term) {
      return term ? `${this.noResultsText} for "${term}"` : this.noResultsText;
    }
  }
};
</script>

<style lang="scss">
/* Vue Material portals autocomplete results to the page body. Its default menu
   layer (60) sits behind dialogs (110), unlike the library's regular selects. */
.md-menu-content {
  z-index: 111 !important;
}
</style>
